import { ReactElement } from 'react'
import { useWatch } from 'react-hook-form'
import { Alert, Stack } from '@mui/material'
import { SubmitButton } from '@ui/forms'
import CheckSettingsResetButton from '../../MultiEditCheckSettings/components/CheckSettingsResetButton'
import { CheckSettingsFormValues } from '../../MultiEditCheckSettings/types'
import {
  SharedSupportCompressionSettings,
  hasIncludedSupport,
  hasStagedSupportCompressionField,
  isSupportCompressionStageValid,
  wouldLeaveSupportWithoutAreaAndOverhang,
} from '../utils'

interface Props {
  isLoading: boolean
  isUpdatingChecks: boolean
}

/**
 * Reset/save row of the support compression section. Saving is disabled while
 * nothing is staged yet or every support was removed from the edit (it would be
 * a no-op), while a staged field is incomplete, and while a staged null would
 * strip some support of both area and overhang (the backend rejects such a
 * config) — the same guards that pause the live recompute. The bare-support
 * warning itself shows up in the fields section, next to what caused it.
 */
const SupportCompressionActions = ({ isLoading, isUpdatingChecks }: Props): ReactElement => {
  const sharedSettings = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'sharedSettings',
  }) as SharedSupportCompressionSettings | undefined
  const elements = useWatch<CheckSettingsFormValues<SharedSupportCompressionSettings>>({
    name: 'elements',
  }) as CheckSettingsFormValues['elements']

  const isStageValid = isSupportCompressionStageValid(sharedSettings)
  const leavesBareSupport = wouldLeaveSupportWithoutAreaAndOverhang(elements, sharedSettings)
  const hasStagedField = hasStagedSupportCompressionField(sharedSettings)
  const hasSupportToEdit = hasIncludedSupport(elements, sharedSettings)

  return (
    <Stack direction="column" spacing={1}>
      {!isStageValid && (
        <Alert severity="warning">Bitte füllen Sie alle Felder der gewählten Einstellung aus</Alert>
      )}
      {!hasSupportToEdit && (
        <Alert severity="warning" data-cy="support-compression-no-support-warning">
          Es ist kein Auflager ausgewählt
        </Alert>
      )}
      <Stack direction="row" spacing={1}>
        <CheckSettingsResetButton<SharedSupportCompressionSettings>
          disabled={isUpdatingChecks || isLoading}
        />
        <SubmitButton
          loading={isLoading}
          disabled={
            isUpdatingChecks ||
            !hasStagedField ||
            !hasSupportToEdit ||
            !isStageValid ||
            leavesBareSupport
          }
        />
      </Stack>
    </Stack>
  )
}

export default SupportCompressionActions
